// Phase 2 §7: Extended CapTableSummary with mode support and edit functionality
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PieChart, Users, Edit, Trash2, TrendingUp } from 'lucide-react';
import { useProjectionMode } from '@/hooks/useProjectionMode';
import { CapTableModeToggle } from './CapTableModeToggle';
import { ShareholderDialog } from '@/components/shareholder/ShareholderDialog';
import { ShareAdjustmentDialog } from './ShareAdjustmentDialog';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useTenantStore } from '@/hooks/useTenantStore';
import { toast } from 'sonner';
import * as CapLedgerService from '@/services/capLedgerService';
import { useCapTableNotifications } from '@/hooks/useCapTableNotifications';
import { ScenarioWatermark } from './ScenarioWatermark';

interface CapTableEntry {
  company_id: string;
  company_name: string;
  shareholder_id: string;
  shareholder_name: string;
  total_shares: number;
}

interface CapTableSummaryProps {
  data: CapTableEntry[];
  loading: boolean;
  onEdit?: () => void;
}

export const CapTableSummary = ({ data, loading, onEdit }: CapTableSummaryProps) => {
  const { isScenarioMode } = useProjectionMode();
  const { tenantId } = useTenantStore();
  const { hasRole } = useUserRoles(tenantId || undefined);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState<any>(null);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingShareholder, setDeletingShareholder] = useState<CapTableEntry | null>(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustingEntry, setAdjustingEntry] = useState<CapTableEntry | null>(null);
  const [authorizedShares, setAuthorizedShares] = useState<number | null>(null);
  const [loadingAuthorized, setLoadingAuthorized] = useState(false);

  // Get company_id from data for notifications
  const companyId = data.length > 0 ? data[0].company_id : tenantId;

  // Enable real-time notifications for cap table changes
  useCapTableNotifications(companyId);

  // Load authorized shares
  useEffect(() => {
    const loadAuthorizedShares = async () => {
      if (!companyId) return;

      setLoadingAuthorized(true);
      try {
        const company = await CapLedgerService.getCompanyById(companyId);
        setAuthorizedShares((company as any).authorized_shares || null);
      } catch (error) {
        console.error('Error loading authorized shares:', error);
      } finally {
        setLoadingAuthorized(false);
      }
    };

    loadAuthorizedShares();
  }, [companyId]);

  const canEditShareholders = hasRole('owner') || hasRole('admin');

  const handleEditClick = async (entry: CapTableEntry) => {
    // The cap table view does not carry entity_type, and ShareholderDialog falls
    // back to 'INDIVIDUAL' when it is absent — so opening and saving a COMPANY or
    // TRUST shareholder silently downgraded it. This previously passed the
    // long-dropped is_entity column, which TypeScript did not catch because
    // editingShareholder is typed `any`. Fetch the real values instead.
    const { data: shareholder } = await supabase
      .from('shareholders')
      .select('email, address, tax_id, entity_type')
      .eq('id', entry.shareholder_id)
      .maybeSingle();

    setEditingShareholder({
      id: entry.shareholder_id,
      name: entry.shareholder_name,
      company_id: entry.company_id,
      email: shareholder?.email ?? null,
      address: shareholder?.address ?? null,
      tax_id: shareholder?.tax_id ?? null,
      entity_type: shareholder?.entity_type ?? 'INDIVIDUAL',
      // Postgres numeric arrives as a string; the dialog compares it against
      // what the user types to decide what movement to record.
      total_shares: Number(entry.total_shares),
    });

    // Set companies list
    setCompanies([{ id: entry.company_id, name: entry.company_name }]);

    setEditDialogOpen(true);
  };

  // Refresh only. Whether the dialog closes is the dialog's own decision, and it
  // has a case where it must not: the shareholder's details save first and the
  // share movement second, so a rejected movement — a cap breach, say — leaves
  // saved details to reflect here while the form stays open with what the user
  // typed. Closing from in here took that away and lost the entry.
  const handleEditSuccess = () => {
    if (onEdit) onEdit();
  };

  const handleDeleteClick = (entry: CapTableEntry) => {
    setDeletingShareholder(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingShareholder) return;

    try {
      await CapLedgerService.deleteShareholder(deletingShareholder.shareholder_id);
      toast.success('Shareholder deleted successfully');
      if (onEdit) onEdit();
    } catch (error) {
      console.error('Error deleting shareholder:', error);
      toast.error('Failed to delete shareholder');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingShareholder(null);
    }
  };

  const handleAdjustClick = (entry: CapTableEntry) => {
    setAdjustingEntry(entry);
    setAdjustDialogOpen(true);
  };

  const handleAdjustSuccess = () => {
    setAdjustDialogOpen(false);
    setAdjustingEntry(null);
    if (onEdit) onEdit();
  };

  if (loading) {
    return (
      <Card className={cn('relative', isScenarioMode && 'border-l-4 border-l-warning')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <CapTableModeToggle showLabels={false} />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={cn('relative', isScenarioMode && 'border-l-4 border-l-warning')}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Cap Table Summary {isScenarioMode && '(Projection)'}
            </CardTitle>
            <CardDescription>Real-time ownership breakdown from ledger events</CardDescription>
          </div>
          <CapTableModeToggle showLabels={false} />
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-10">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No cap table data available</p>
          <p className="text-sm text-muted-foreground">Record ledger events to see ownership</p>
        </CardContent>
      </Card>
    );
  }

  const totalShares = data.reduce((sum, entry) => sum + Number(entry.total_shares), 0);

  return (
    <Card
      className={cn('relative overflow-hidden', isScenarioMode && 'border-l-4 border-l-warning')}
    >
      {isScenarioMode && <ScenarioWatermark />}
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex-1">
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            Cap Table Summary {isScenarioMode && '(Projection)'}
          </CardTitle>
          <CardDescription>Real-time ownership breakdown from ledger events</CardDescription>
          {authorizedShares !== null && !loadingAuthorized && !isScenarioMode && (
            <div className="flex items-center gap-2 mt-2 text-sm">
              <span
                className={cn(
                  'font-medium',
                  totalShares > authorizedShares ? 'text-destructive' : 'text-muted-foreground'
                )}
              >
                {totalShares.toLocaleString()} / {authorizedShares.toLocaleString()} shares issued
              </span>
              {totalShares > authorizedShares && (
                <span className="text-destructive font-semibold">⚠️ Cap exceeded</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CapTableModeToggle showLabels={false} />
        </div>
      </CardHeader>
      <CardContent className="relative z-10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Shareholder</TableHead>
              <TableHead>Company</TableHead>
              <TableHead className="text-right">Shares</TableHead>
              <TableHead className="text-right">Ownership %</TableHead>
              {canEditShareholders && !isScenarioMode && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry) => {
              const ownership =
                totalShares > 0 ? (Number(entry.total_shares) / totalShares) * 100 : 0;
              return (
                <TableRow key={`${entry.shareholder_id}-${entry.company_id}`}>
                  <TableCell className="font-medium">{entry.shareholder_name}</TableCell>
                  <TableCell>{entry.company_name}</TableCell>
                  <TableCell className="text-right">
                    {Number(entry.total_shares).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">{ownership.toFixed(2)}%</TableCell>
                  {canEditShareholders && !isScenarioMode && (
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {/* Labelled, not an icon: this is the action that moves
                            shares, and as a bare chart glyph between a pencil
                            and a bin nobody found it. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAdjustClick(entry)}
                          title="Adjust shares"
                        >
                          <TrendingUp className="h-4 w-4 sm:mr-1.5" />
                          <span className="hidden sm:inline">Adjust shares</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(entry)}
                          title="Edit shareholder"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(entry)}
                          title="Delete shareholder"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            <TableRow className="font-semibold bg-muted/50">
              <TableCell colSpan={2}>Total</TableCell>
              <TableCell className="text-right">{totalShares.toLocaleString()}</TableCell>
              <TableCell className="text-right">100.00%</TableCell>
              {canEditShareholders && !isScenarioMode && <TableCell></TableCell>}
            </TableRow>
          </TableBody>
        </Table>

        <ShareholderDialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            // Dropped only once the dialog is actually shut, so it survives a
            // save that half-succeeded and left the form open.
            if (!open) setEditingShareholder(null);
          }}
          onSuccess={handleEditSuccess}
          companies={companies}
          editingShareholder={editingShareholder}
        />

        {adjustingEntry && (
          <ShareAdjustmentDialog
            open={adjustDialogOpen}
            onOpenChange={setAdjustDialogOpen}
            onSuccess={handleAdjustSuccess}
            shareholderId={adjustingEntry.shareholder_id}
            shareholderName={adjustingEntry.shareholder_name}
            companyId={adjustingEntry.company_id}
            currentShares={adjustingEntry.total_shares}
          />
        )}

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Shareholder</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {deletingShareholder?.shareholder_name}? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
